import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import { useNavigate } from 'react-router-dom';
import { useQueryState } from 'react-router-use-location-state';
import { Button, Card, Spinner } from 'react-bootstrap';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiArrowRight, FiArrowLeft, FiClock, FiUsers } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

registerLocale('es', es);
moment.locale('es');

export default function Journey() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [date, setDate] = useState(new Date());
    const [, setTrips] = useState([]);
    const [tripsGo, setTripsGo] = useState([]);
    const [tripsCome, setTripsCome] = useState([]);
    const [queryDate, setQueryDate] = useQueryState("date", "");
    const [loading, setLoading] = useState(false);

    const loadTrips = (currentDate) => {
        if (!currentDate) return;

        setLoading(true);
        const formattedDate = currentDate.toISOString().split('T')[0];

        tre.api('GET', '/trips/journeys', {
            context: context,
            params: { date: formattedDate },
            success: (r) => {
                if (!Array.isArray(r)) {
                    setTrips([]);
                    setTripsGo([]);
                    setTripsCome([]);
                    setLoading(false);
                    return;
                }

                setTrips(r);
                setTripsGo(r.filter(trip => trip?.route?.direction === 'GO') || []);
                setTripsCome(r.filter(trip => trip?.route?.direction === 'COME') || []);
                setLoading(false);
            },
            error: () => {
                setTrips([]);
                setTripsGo([]);
                setTripsCome([]);
                setLoading(false);
            },
        });
    };

    useEffect(() => {
        if (queryDate) {
            const parsedDate = moment(queryDate + ' 12:00:00').toDate();
            setDate(parsedDate);
            loadTrips(parsedDate);
        } else {
            const today = new Date();
            setDate(today);
            loadTrips(today);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryDate, context.user.role]);

    const handleDateChange = (newDate) => {
        if (newDate && newDate !== date) {
            setDate(newDate);
            setQueryDate(moment(newDate).format('YYYY-MM-DD'));
            loadTrips(newDate);
        }
    };

    const onSelectTrip = (trip) => {
        if (['ADMIN', 'DRIVER'].includes(context.user.role)) {
            navigate(`/trip/${trip.id}`);
        }
    };

    const today = () => handleDateChange(new Date());
    const prevDate = () => handleDateChange(moment(date).subtract(1, 'day').toDate());
    const nextDate = () => handleDateChange(moment(date).add(1, 'day').toDate());

    const isToday = moment(date).isSame(moment(), 'day');
    const formattedDate = moment(date).format('dddd, D [de] MMMM');

    const TripCard = ({ trip, variant }) => {
        const isGo = variant === 'go';
        const bgColor = isGo ? '#e8f0fe' : '#e6f4ea';
        const textColor = isGo ? '#1a73e8' : '#34a853';
        const hasPassengers = trip.travellers?.length > 0;

        return (
            <Card
                className="mb-3 border-0 overflow-hidden"
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    borderRadius: '12px'
                }}
                onClick={() => onSelectTrip(trip)}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
            >
                <div style={{ height: '4px', backgroundColor: textColor }} />
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    backgroundColor: bgColor
                                }}
                            >
                                <FiClock style={{ color: textColor, fontSize: '20px' }} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 500, fontSize: '1.1rem', color: '#202124' }}>
                                    {trip.name || trip.route?.name || 'Sin nombre'}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#5f6368' }}>
                                    Salida: <span style={{ fontWeight: 600, color: textColor }}>
                                        {moment(trip.route?.departure, 'HH:mm:ss').format('HH:mm')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="text-end">
                            <span
                                className="d-flex align-items-center px-3 py-2"
                                style={{
                                    backgroundColor: hasPassengers ? textColor : '#f1f3f4',
                                    color: hasPassengers ? 'white' : '#5f6368',
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                    borderRadius: '20px'
                                }}
                            >
                                <FiUsers className="me-1" />
                                {trip.travellers?.length || 0}
                            </span>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const EmptyState = ({ message }) => (
        <Card className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
            <Card.Body className="text-center py-4">
                <div style={{ color: '#5f6368' }}>{message}</div>
            </Card.Body>
        </Card>
    );

    return (
        <div className="container-fluid py-4" style={{ maxWidth: '900px' }}>
            {/* Date Navigation Header */}
            <Card className="border-0 mb-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <Card.Body className="p-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                        <div className="d-flex align-items-center mb-3 mb-md-0">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{ width: '48px', height: '48px', backgroundColor: '#e8f0fe' }}
                            >
                                <FiCalendar style={{ color: '#1a73e8', fontSize: '22px' }} />
                            </div>
                            <div>
                                <h5 className="mb-0 text-capitalize" style={{ fontWeight: 500, color: '#202124' }}>
                                    {formattedDate}
                                </h5>
                                {isToday && (
                                    <span
                                        style={{
                                            backgroundColor: '#e6f4ea',
                                            color: '#34a853',
                                            padding: '2px 10px',
                                            borderRadius: '10px',
                                            fontSize: '0.8rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        Hoy
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="light"
                                onClick={prevDate}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '42px', height: '42px', borderRadius: '10px', border: '1px solid #e9ecef' }}
                            >
                                <FiChevronLeft />
                            </Button>
                            <DatePicker
                                className="form-control text-center"
                                dateFormat="dd/MM/yyyy"
                                selected={date}
                                locale="es"
                                onChange={handleDateChange}
                                style={{ width: '140px', borderRadius: '10px' }}
                            />
                            <Button
                                variant="light"
                                onClick={nextDate}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '42px', height: '42px', borderRadius: '10px', border: '1px solid #e9ecef' }}
                            >
                                <FiChevronRight />
                            </Button>
                            {!isToday && (
                                <Button
                                    onClick={today}
                                    className="d-flex align-items-center ms-2"
                                    style={{
                                        backgroundColor: '#1a73e8',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '10px 16px',
                                        fontWeight: 500
                                    }}
                                >
                                    <FiCalendar className="me-2" /> Hoy
                                </Button>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner
                        animation="border"
                        style={{
                            width: '3rem',
                            height: '3rem',
                            color: '#1a73e8'
                        }}
                    />
                    <div className="mt-3" style={{ color: '#5f6368' }}>Cargando viajes...</div>
                </div>
            ) : (
                <div className="row g-4">
                    {/* IDA Section */}
                    <div className="col-12 col-lg-6">
                        <div className="d-flex align-items-center mb-3">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: '#e8f0fe'
                                }}
                            >
                                <FiArrowRight style={{ color: '#1a73e8', fontSize: '16px' }} />
                            </div>
                            <h6 className="mb-0" style={{ fontWeight: 500, color: '#1a73e8' }}>IDA</h6>
                            <span
                                className="ms-2"
                                style={{
                                    backgroundColor: '#f1f3f4',
                                    color: '#5f6368',
                                    padding: '4px 10px',
                                    borderRadius: '10px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500
                                }}
                            >
                                {tripsGo.length}
                            </span>
                        </div>

                        {tripsGo.length > 0 ? (
                            tripsGo.map((trip, idx) => (
                                <TripCard key={idx} trip={trip} variant="go" />
                            ))
                        ) : (
                            <EmptyState message="No hay viajes de ida programados" />
                        )}
                    </div>

                    {/* VUELTA Section */}
                    <div className="col-12 col-lg-6">
                        <div className="d-flex align-items-center mb-3">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: '#e6f4ea'
                                }}
                            >
                                <FiArrowLeft style={{ color: '#34a853', fontSize: '16px' }} />
                            </div>
                            <h6 className="mb-0" style={{ fontWeight: 500, color: '#34a853' }}>VUELTA</h6>
                            <span
                                className="ms-2"
                                style={{
                                    backgroundColor: '#f1f3f4',
                                    color: '#5f6368',
                                    padding: '4px 10px',
                                    borderRadius: '10px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500
                                }}
                            >
                                {tripsCome.length}
                            </span>
                        </div>

                        {tripsCome.length > 0 ? (
                            tripsCome.map((trip, idx) => (
                                <TripCard key={idx} trip={trip} variant="come" />
                            ))
                        ) : (
                            <EmptyState message="No hay viajes de vuelta programados" />
                        )}
                    </div>
                </div>
            )}

            {/* Custom DatePicker styles */}
            <style>{`
                .react-datepicker-wrapper {
                    width: auto;
                }
                .react-datepicker__input-container input {
                    border-radius: 10px;
                    border: 1px solid #e9ecef;
                    padding: 10px 15px;
                    width: 130px;
                    transition: border-color 0.2s;
                    font-weight: 500;
                }
                .react-datepicker__input-container input:focus {
                    border-color: #1a73e8;
                    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
                    outline: none;
                }
                .react-datepicker {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
                }
                .react-datepicker__header {
                    background: #1a73e8;
                    border: none;
                    border-radius: 12px 12px 0 0;
                    padding-top: 12px;
                }
                .react-datepicker__current-month,
                .react-datepicker__day-name {
                    color: white;
                }
                .react-datepicker__day--selected {
                    background: #1a73e8;
                    border-radius: 50%;
                }
                .react-datepicker__day:hover {
                    background: #e8f0fe;
                    border-radius: 50%;
                }
                .react-datepicker__navigation-icon::before {
                    border-color: white;
                }
            `}</style>
        </div>
    );
}
